import type BaseEnumType from "@/app/services/baseEnumType";
import type Guid from "@/app/types/common/guid";
import userStore from "@/store/modules/userStore";
import {
  createJsonParseReviver,
  createJsonStringifyReplacer,
} from "@/app/storageReplacerReviverUtils";
import type { LocalDate } from "@js-joda/core";
import router from "@/router/router";
import { watch } from "vue";

export type BmUiStateTypes =
  | LocalDate[]
  | BaseEnumType[]
  | BaseEnumType
  | boolean
  | Guid[]
  | Guid
  | string
  | string[]
  | undefined;

export interface BmUiState {
  [key: string]: BmUiStateTypes | Readonly<BmUiStateTypes>;
}

export class BmUiStateRepository {
  public static load<T extends BmUiState>(key: string): Partial<T> {
    const item =
      localStorage.getItem(
        BmUiStateRepository.calculateStorageKeyForRequest()
      ) ?? "{}";
    const storage = JSON.parse(item, createJsonParseReviver());
    return storage[key] ?? {};
  }

  public static save<T extends BmUiState>(key: string, state: T): void {
    const storage = JSON.parse(
      localStorage.getItem(
        BmUiStateRepository.calculateStorageKeyForRequest()
      ) ?? "{}"
    );
    storage[key] = state;
    localStorage.setItem(
      BmUiStateRepository.calculateStorageKeyForRequest(),
      JSON.stringify(storage, createJsonStringifyReplacer())
    );
  }

  public static preserve<T extends BmUiState>(
    key: string,
    extract: () => T,
    restore: (state: Partial<T>) => void
  ) {
    watch(
      () => extract(),
      (newState) => BmUiStateRepository.save(key, newState)
    );
    restore(BmUiStateRepository.load(key));
  }

  private static calculateStorageKeyForRequest() {
    return `${userStore.state.user?.id.toString() ?? ""}-bmuistate`.replace(
      /^-/,
      ""
    );
  }

  public static getKeyForCurrentRoute() {
    const currentRouteName = router.currentRoute.name;
    if (!currentRouteName) {
      // eslint-disable-next-line no-console
      console.error(
        "Key requested for current route, when no route is available."
      );
      return "";
    }

    return currentRouteName;
  }
}
